"use client";
import React, { useState } from "react";
import { usePathname } from "next/navigation";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/stories/accordian/accordian";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import ICMWrapper from "@/app/components/icm-wrapper";
import { fetchFaqData, resolveFaqParams } from "@/app/api/service/faq";
import { useQuery } from "@tanstack/react-query";
import useMobile from "@/app/hooks/use-mobile";
import parse from "html-react-parser";
import type { ProductFaqData } from "@/types/faq.model";

interface QuestionType {
    ques: string;
    ans: string;
}

interface SectionType {
    section: string;
    content: QuestionType[];
    isEditing: boolean;
}

interface FaqProps {
    bgColor: string;
    compact: boolean;
    /** API productType query param (e.g. others, fd, bonds, unlistedEquities, calculator, bullion). */
    product?: string;
    /** API path segment (e.g. generic, referral, NSE01, SURYIN). */
    subProduct?: string;
    /** Server-fetched FAQ data for SSR / hardcoded flows (global, bullion). Skips client fetch when set. */
    initialFaqData?: ProductFaqData | null;
}

const Faq: React.FC<FaqProps> = ({ bgColor, compact, product, subProduct, initialFaqData }) => {
    const pathName = usePathname();
    const { isMobile } = useMobile();
    const [showAllSections, setShowAllSections] = useState(false);
    const [showAllQuestions, setShowAllQuestions] = useState(false);

    const { product: resolvedProduct, subProduct: resolvedSubProduct } = resolveFaqParams(
        product,
        subProduct,
    );
    const usesInitialDataOnly =
        resolvedProduct === "icu-transfer" || resolvedProduct === "global" || (resolvedProduct === "bullion" && !!initialFaqData);

    const { data: fetchedFaqData, isFetched } = useQuery({
        queryKey: ["faqData", resolvedProduct, resolvedSubProduct],
        queryFn: () => fetchFaqData(resolvedProduct, resolvedSubProduct),
        enabled: !usesInitialDataOnly,
        initialData: usesInitialDataOnly && initialFaqData ? initialFaqData : undefined,
    });
    
    const faqData = usesInitialDataOnly ? initialFaqData : fetchedFaqData;
    const isFaqReady = usesInitialDataOnly ? !!initialFaqData : isFetched;
    
    const handleToggleSections = () => {
        setShowAllSections((prev) => !prev);
    };

    const handleToggleQuestions = () => {
        setShowAllQuestions((prev) => !prev);
    };

    const activeSections = faqData?.data?.questions || [];
    const displayedSections = showAllSections ? activeSections : activeSections.slice(0, 4);
    const showToggleButton = activeSections.length > 5;
    const isSingleSection = activeSections.length === 1;

    const renderQuestions = (questions: QuestionType[], isWithinSection: boolean = false) => {
        const shouldShowToggle = questions.length > 4 && isSingleSection && !isWithinSection;
        const displayedQuestions = isWithinSection
            ? questions
            : showAllQuestions
              ? questions
              : questions.slice(0, 4);

        return (
            <>
                <Accordion
                    id="section-child"
                    type="single"
                    collapsible
                    className={`mx-0 w-full md:mx-auto max-w-[900px] md:w-full ${bgColor}`}
                >
                    {displayedQuestions.map((item, index) => (
                        <AccordionItem
                            id={"section-child-item-" + (index + 1)}
                            key={index}
                            value={`question-${index}`}
                            className={`w-full border-none ${bgColor}`}
                        >
                            <AccordionTrigger
                                id={"section-child-q-" + (index + 1)}
                                className="!min-h-[58px] w-full py-s7 text-left text-md font-semibold leading-[22.4px] tracking-[-0.18px] text-neutral-900 md:!min-h-[78px] md:text-lg md:leading-[29.4px]"
                            >
                                <span className="block pr-1 md:pr-0">{item.ques}</span>
                            </AccordionTrigger>
                            <AccordionContent
                                id={"section-child-a-" + (index + 1)}
                                className="border-b border-neutralOpacityDark-20 pb-s9 pr-s10 pt-s2 text-left text-sm font-regular leading-[22.4px] tracking-[0.14px] text-neutral-500 md:pr-s15"
                            >
                                {parse(item.ans)}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
                {shouldShowToggle && (
                    <div className="mt-4 flex items-center justify-center">
                        <RegularButton
                            id="btn-show-less-more"
                            noIcon
                            showRightIcon={false}
                            size={ButtonSize.Large}
                            variant={ButtonVariant.SecondaryDefault}
                            label={showAllQuestions ? "Show Less" : "Show More"}
                            onClick={handleToggleQuestions}
                            className="mt-8 w-full max-w-full md:w-auto"
                        />
                    </div>
                )}
            </>
        );
    };

    const renderSections = (sections: SectionType[]) => {
        if (sections.length === 1) {
            return renderQuestions(sections[0].content);
        }

        return (
            <Accordion
                id="section-main"
                type="single"
                collapsible
                className={`mx-0 w-full md:mx-auto max-w-[900px] ${bgColor} ${resolvedProduct === "bullion" ? "md:!w-full" : ""}`}
            >
                {sections.map((section, index) => (
                    <AccordionItem
                        id={"section-main-item-" + (index + 1)}
                        key={index}
                        value={`section-${index}`}
                        className={`${bgColor ? bgColor : "bg-white"} w-full !border-none px-0`}
                    >
                        <AccordionTrigger
                            id={"section-main-q-" + (index + 1)}
                            className="!h-[58px] border-b border-neutralOpacityDark-20 py-s7 text-left text-md font-medium leading-[22.4px] tracking-[-0.18px] text-neutral-900 md:!min-h-[78px] md:text-lg md:leading-[29.4px]"
                        >
                            <span className="block pr-1 md:pr-0">{section.section}</span>
                        </AccordionTrigger>
                        <AccordionContent id={"section-main-child-c" + (index + 1)} className="p-0">
                            {renderQuestions(section.content, true)}
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        );
    };

    const renderToggleButton = (id: string) => (
        <RegularButton
            id={id}
            noIcon
            showRightIcon={false}
            size={isMobile ? ButtonSize.Medium : ButtonSize.Large}
            variant={ButtonVariant.SecondaryDefault}
            label={showAllSections ? "Show Less" : "Show More"}
            onClick={handleToggleSections}
            className="mt-8 w-full max-w-full md:w-auto"
        />
    );

    if (!isFaqReady) {
        return null;
    }

    if (compact) {
        return (
            <div id="section-faq" className={bgColor}>
                <div
                    className={`mt-16 w-full md:mb-12 ${resolvedProduct !== "calculator" ? "" : ""} mb-4`}
                >
                    {" "}
                    {/* max-w-[812px] */}
                    <h2
                        id="section-title"
                        className={`mb-2 ${resolvedProduct === "calculator" ? "text-xl md:text-4xl" : "text-3xl"} font-semibold`}
                    >
                        FAQs
                    </h2>
                    <div className="pb-s9 md:pb-s14">{renderSections(displayedSections)}</div>
                    {showToggleButton && !isSingleSection && (
                        <div className="flex items-center justify-center">
                            {renderToggleButton("section-btn-show-less-more")}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div id="section-faq" className={bgColor}>
            <ICMWrapper>
                <div
                    className={`mx-auto pb-s15 pt-s11 text-center md:px-8 md:py-s20 ${resolvedProduct === "bullion" ? "icm-container md:-mx-6 md:!px-0" : ""}`}
                >
                    <h2
                        id="section-title"
                        className="mb-s9 text-4xl font-semibold leading-[120%] tracking-[-0.48px] md:text-6xl"
                    >
                        {pathName.includes("/unlisted-shares") ? "Unlisted Shares FAQs" : "FAQs"}
                    </h2>
                    <div className="md:px-8 md:pt-s9">{renderSections(displayedSections)}</div>
                    {showToggleButton && !isSingleSection && (
                        <div className="flex items-center justify-center">
                            {renderToggleButton("section-btn-show-less-more")}
                        </div>
                    )}
                </div>
            </ICMWrapper>
        </div>
    );
};

export default Faq;
